---
title: "Moving Averages (SMA/EMA) and How Traders Use Them"
slug: moving-averages
shortTitle: "Moving Averages"
description: "How simple and exponential moving averages are calculated, how traders use them for trend, pullbacks and crossovers, and where they mislead."
level: intermediate
category: trading
course: intermediate
order: 3
topics: [indicators, technical-analysis]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/moving-averages.svg
faqs:
  - q: "Which is better, the SMA or the EMA?"
    a: "Neither is better in general. The EMA reacts faster to new prices, so it turns sooner but also gives more false turns, while the SMA is smoother and slower. Pick one, learn how it behaves on the markets you trade, and stay consistent."
  - q: "Which moving average lengths do traders watch most?"
    a: "The 20, 50 and 200 periods are the most widely watched, with 9 or 10 sometimes used for very short-term momentum. The timeframe matters as much as the length: a 50-period average on a 1-hour chart covers about two days, while on a daily chart it covers about ten weeks."
  - q: "Is a golden cross a buy signal?"
    a: "It describes what has already happened: the 50-day average has risen above the 200-day average. Because both averages lag, the cross often arrives well after the move began, and in sideways markets crosses can flip back and forth. Treat it as context, not as an instruction to trade."
  - q: "Can I add moving averages to the chart on the trade screen?"
    a: "The trade chart does not currently plot indicators. The Markets page shows moving-average readings in the quick view and the opportunity scanner, and you can plot averages in a separate charting tool while placing your orders on the platform."
  - q: "Do moving averages work on crypto as well as on gold or indices?"
    a: "The calculation is the same on any market with a price history. How useful it is depends on how that market behaves: averages help most when a market trends and least when it chops sideways, whatever the asset."
---

Prices rarely move in a straight line. Even in a strong trend, a chart is full of spikes, dips and pauses that make the bigger picture hard to see. A **moving average** smooths that noise into a single line by averaging recent closing prices, then sliding forward one candle at a time. It is one of the oldest tools in technical analysis, and it sits behind many of the trend readings you will see on the Markets page.

This lesson shows how the two main types, the simple and the exponential moving average, are calculated, the four ways traders commonly use them, and where they mislead. As with every indicator, a moving average describes what price has done. It does not tell you what price will do next.

<CTA label="See trend readings on Markets" to="/markets" />

<KeyTakeaways>

- **An average of recent closes.** A moving average turns noisy price data into a smooth line that shows the direction and pace of the trend.
- **SMA is steady, EMA is quick.** The simple average weights every close equally; the exponential average leans on the latest closes, so it turns sooner.
- **Four common uses.** Traders use averages as a trend filter, as moving support and resistance, for crossovers, and to judge when price is stretched.
- **Lag is the price of smoothness.** Averages confirm moves late and misfire in sideways markets, so pair them with price structure and a stop loss.

</KeyTakeaways>

## What a moving average is

A moving average takes the closing prices of the last *n* candles, averages them and plots the result. When a new candle closes, the oldest close drops out and the newest comes in, so the average "moves" along the chart.

The number *n* is the **length**, or period. A 20-period average on a daily chart averages the last 20 daily closes; on a 1-hour chart it averages the last 20 hourly closes. Short lengths hug the price and react quickly. Long lengths are smoother and slower, and show the broader trend.

## The simple moving average (SMA)

The **simple moving average** adds up the closes and divides by the number of periods. Every close counts the same.

<Example title="A 5-day SMA on ETH/USDT (illustrative prices)">

The last five daily closes are 3,000, 3,060, 3,030, 3,090 and 3,120.

- Sum: 3,000 + 3,060 + 3,030 + 3,090 + 3,120 = 15,300
- SMA: 15,300 ÷ 5 = **3,060 USDT**

The next day closes at 3,180. The oldest close (3,000) drops out and 3,180 comes in:

- New sum: 15,300 − 3,000 + 3,180 = 15,480
- New SMA: 15,480 ÷ 5 = **3,096 USDT**

</Example>

The SMA rose partly because a low, old price left the window, not only because a high new price arrived. This drop-off effect can make an SMA jump or stall for reasons that have little to do with today's market.

## The exponential moving average (EMA)

The **exponential moving average** gives the newest close the most weight and lets older closes fade gradually instead of falling out all at once. It uses a smoothing factor, *k* = 2 ÷ (*n* + 1). For a 5-period EMA, *k* = 2 ÷ 6, about 0.33.

Each new value is: **EMA = previous EMA + k × (close − previous EMA)**.

<Example title="The same day with a 5-day EMA (illustrative prices)">

Say yesterday's 5-day EMA was 3,060 and today closes at 3,180.

- Gap between the close and the EMA: 3,180 − 3,060 = 120
- Adjustment: 120 × 0.33 ≈ 40
- New EMA: 3,060 + 40 = **3,100 USDT**

</Example>

The EMA moved 40 points against the SMA's 36 from the same close. Over a run of strong closes that gap grows, which is why an EMA turns earlier at the start of a move, and also why one wild candle shakes it more.

<CompareTable caption="Simple and exponential moving averages compared">

| | SMA | EMA |
| --- | --- | --- |
| Weighting | Every close counts equally | Recent closes count more |
| Speed | Slower to turn | Faster to turn |
| Noise | Smoother, fewer false turns | More sensitive, more false turns |
| Typical use | Longer-term trend (50, 200) | Shorter-term trend and pullbacks (9, 20) |

</CompareTable>

## Four ways traders use moving averages

### 1. As a trend filter

The simplest use is to check which side of an average the price is on, and which way the average slopes. Price above a rising 50-period average suggests an uptrend; price below a falling one suggests a downtrend. Many traders only look for buy setups in the first case and sell setups in the second. A flat average with price weaving through it says there is no clear trend at all.

### 2. As moving support and resistance

In a steady trend, price often pulls back towards a shorter average, such as the 20-period, before continuing. Some traders treat that area as a zone to look for an entry, with a stop loss beyond the most recent swing. It is the same logic as the horizontal levels in [Support and Resistance](/academy/article/support-and-resistance): a zone, not an exact line.

### 3. Crossovers

A **crossover** happens when a faster average crosses a slower one. Fast crossing above slow shows that recent prices have risen above the longer-run average; crossing below shows the opposite. On daily charts, the 50-day crossing above the 200-day is often called a **golden cross**, and crossing below a **death cross**. Because both averages lag, a cross usually confirms a move that is already well underway.

### 4. Measuring stretch

When price runs far from its average, some traders see the market as stretched. You can measure the distance in percent: a close of 3,300 against a 20-day average of 3,000 is (3,300 − 3,000) ÷ 3,000 = 10% above it. Mean-reversion traders look for a move back towards the average; trend traders wait for a pullback rather than buying the extreme. [Breakout vs Mean Reversion Strategies](/academy/article/breakout-vs-mean-reversion) covers both mindsets.

<ProTip>

Choose one or two averages and keep them for months. Switching lengths after every losing trade is curve-fitting: you end up with settings that explain the past perfectly and say little about the next trade.

</ProTip>

## Where moving averages mislead

Every moving average is built from past prices, so it always **lags**, and the longer the average, the bigger the lag. That is tolerable in a trending market, where being late but on the right side can still work. It is costly in a **ranging** market, where price swings back and forth across a flat average and crossovers fire in both directions.

<Warning>

In a sideways market, a crossover approach can produce a string of small losses as signals flip back and forth, and leverage makes that string add up faster than it looks. Set a stop loss on every trade, size it from the amount you are willing to lose, and remember that one line crossing another is not, on its own, a reason to open a position.

</Warning>

Averages are also blind to news. A surprise data release or a crypto event can carry price far beyond any average in seconds. Combine them with price structure, as described in [Trendlines, Channels and Market Structure](/academy/article/trendlines-channels-market-structure), rather than using them alone.

## Moving averages on {{BRAND}}

The chart on the trade screen focuses on price and your orders; it does not currently plot indicators or drawing tools. Moving-average readings appear on the **Markets** page instead:

- The **quick view** for a market has a **Daily trend** section showing whether price is above or below its 20-, 50- and 200-day averages, and whether the 50-day is above the 200-day.
- The **Opportunity scanner** has screens such as **MA cross up** (a shorter hourly average crossing above a longer one) and **50/200 cross up** or **50/200 cross down** on daily candles. They are screens, not advice.
- The **Signal** column on the market list shows the latest screen that fired or, when none has, a simple trend reading.

{/* TODO: verify with platform — trade chart has no indicators; quick view Daily trend labels, scanner screen names and the Signal column wording. */}

<Screenshot name="markets-quick-view-daily-trend" alt="The Markets quick view showing price against its 20-, 50- and 200-day averages" caption="The Daily trend section summarizes where price sits against its main averages." />

To see the lines themselves, or to try your own lengths, you can plot averages in a separate charting tool and place and manage your orders on {{BRAND}}. Just make sure both show the same market and timeframe.

<Steps>
<Step title="Check the trend context">

On the Markets page, open the quick view for the market and note where price sits against its main averages.

</Step>
<Step title="Decide which side you will look at">

A common approach is to look only for buy setups when the averages point up, only for sell setups when they point down, and to wait when they are mixed.

</Step>
<Step title="Plan the trade on the trade screen">

Pick your entry, place a stop loss beyond a recent swing, and enter it with the **TP / SL** switch before you confirm.

</Step>
</Steps>

## Conclusion

A moving average is an average of recent closes that slides forward with each candle. The SMA weights every close equally and moves steadily; the EMA leans on recent prices and turns sooner. Traders use them to filter trend direction, find pullback zones, read crossovers and judge when price is stretched. Their weakness is lag, which hurts most in sideways markets. Use them as context alongside price structure, keep your settings consistent, and protect every trade with a stop loss sized to what you can afford to lose.
